import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, CalendarDays, Clock, Users, MapPin,
  AlertTriangle, Search, Send, CheckCircle, XCircle,
  Star, Banknote, Percent, Award, FileText, Zap, Lock,
  Save, UserCheck, ShieldCheck, Phone, Copy, Share2, ExternalLink,
  FileSignature, Download
} from "lucide-react";
import { ALL_SPORTS } from "@/lib/sports";

const EVENT_TYPES = [
  { value: "training", label: "Training" },
  { value: "coaching", label: "Coaching" },
  { value: "class", label: "Sports Class" },
  { value: "clinic", label: "Clinic" },
  { value: "fitness", label: "Fitness / Zumba" },
  { value: "dance", label: "Dance" },
  { value: "tournament", label: "Tournament" },
  { value: "other", label: "Other" },
];

export default function OrganizeEvent() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!eventId;
  const isReadOnly = searchParams.get("readonly") === "true";

  const [venues, setVenues] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(isEdit);
  const [existingEvent, setExistingEvent] = useState(null);

  // Attendees
  const [attendees, setAttendees] = useState([]);
  const [attendeeMeta, setAttendeeMeta] = useState({ total: 0, capacity: 0 });
  const [loadingAttendees, setLoadingAttendees] = useState(false);

  // Coach
  const [coaches, setCoaches] = useState([]);
  const [coachSearch, setCoachSearch] = useState("");
  const [searchingCoaches, setSearchingCoaches] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [showOfferForm, setShowOfferForm] = useState(false);

  const [form, setForm] = useState({
    title: "", description: "", sport_type: "basketball", event_type: "training",
    venue_id: "", physical_space_id: "", resource_id: "", space_name: "",
    event_date: "", start_time: "09:00", end_time: "12:00",
    capacity: 20, registration_type: "free", price: 0, currency: "PHP", image: "",
    custom_waiver_terms: "",
  });

  const [offer, setOffer] = useState({
    rate_per_hour: "", revenue_pct: "", rating_bonus: "", rating_threshold: 4.0, notes: "",
  });

  // Load venues
  useEffect(() => {
    api.get("/provider/venues").then(r => setVenues(r.data)).catch(() => {});
  }, []);

  // Load event data if editing
  useEffect(() => {
    if (!eventId) return;
    const loadEvent = async () => {
      try {
        const res = await api.get(`/events/${eventId}`);
        const e = res.data;
        setExistingEvent(e);
        setForm({
          title: e.title || "", description: e.description || "",
          sport_type: e.sport_type || "basketball", event_type: e.event_type || "training",
          venue_id: e.venue_id || "", physical_space_id: e.physical_space_id || "",
          resource_id: e.resource_id || "", space_name: e.space_name || "",
          event_date: e.event_date || "", start_time: e.start_time || "09:00",
          end_time: e.end_time || "12:00", capacity: e.capacity || 20,
          registration_type: e.registration_type || "free", price: e.price || 0,
          currency: e.currency || "PHP", image: e.image || "",
          custom_waiver_terms: e.custom_waiver_terms || "",
        });
        if (e.coach_id) {
          setSelectedCoach({
            user_id: e.coach_id, name: e.coach_name || "", email: e.coach_email || "",
            default_price: e.coach_offer?.rate_per_hour || 0, coach_rating: 0,
            sport_specialties: [], sessions_hosted: 0, total_ratings: 0,
          });
          setCoachSearch(e.coach_name || "");
          if (e.coach_offer) {
            setOffer({
              rate_per_hour: e.coach_offer.rate_per_hour || "",
              revenue_pct: e.coach_offer.revenue_pct || "",
              rating_bonus: e.coach_offer.rating_bonus || "",
              rating_threshold: e.coach_offer.rating_threshold || 4.0,
              notes: e.coach_offer.notes || "",
            });
            setShowOfferForm(true);
          }
        }
      } catch { toast.error("Failed to load event"); }
      setLoadingEvent(false);
    };
    loadEvent();
  }, [eventId]);

  // Load attendees for published events
  useEffect(() => {
    if (!eventId || !existingEvent) return;
    if (existingEvent.status === "ACTIVE") {
      setLoadingAttendees(true);
      api.get(`/events/${eventId}/attendees`).then(r => {
        setAttendees(r.data.attendees || []);
        setAttendeeMeta({ total: r.data.total, capacity: r.data.capacity });
      }).catch(() => {}).finally(() => setLoadingAttendees(false));
    }
  }, [eventId, existingEvent]);

  // Fetch spaces when venue changes
  useEffect(() => {
    if (!form.venue_id) { setSpaces([]); return; }
    const fetchSpaces = async () => {
      let seSpaces = [];
      try {
        const res = await api.get(`/space-engine/venues/${form.venue_id}`);
        seSpaces = (res.data?.spaces || res.data?.physical_spaces || []).map(s => ({ id: s.physical_space_id, name: s.space_name, type: "physical_space" }));
      } catch {}
      const v = venues.find(ven => ven.venue_id === form.venue_id);
      const legacyRes = (v?.resources || []).filter(r => r.status !== "INACTIVE").map(r => ({ id: r.resource_id, name: r.name, type: "resource" }));
      setSpaces([...seSpaces, ...legacyRes]);
    };
    fetchSpaces();
  }, [form.venue_id, venues]);

  const searchCoaches = async (q) => {
    if (!q || q.length < 2) return;
    setSearchingCoaches(true);
    try { const res = await api.get("/coaches/search", { params: { q } }); setCoaches(res.data); }
    catch { setCoaches([]); }
    setSearchingCoaches(false);
  };

  const selectCoach = (coach) => {
    setSelectedCoach(coach); setCoaches([]); setCoachSearch(coach.name); setConflicts(null);
    if (coach.default_price) setOffer(o => ({ ...o, rate_per_hour: coach.default_price }));
  };
  const clearCoach = () => {
    setSelectedCoach(null); setCoachSearch("");
    setOffer({ rate_per_hour: "", revenue_pct: "", rating_bonus: "", rating_threshold: 4.0, notes: "" });
    setShowOfferForm(false);
  };
  const handleSpaceChange = (spaceId) => {
    const sp = spaces.find(s => s.id === spaceId);
    if (sp?.type === "physical_space") setForm(f => ({ ...f, physical_space_id: spaceId, resource_id: "", space_name: sp.name }));
    else setForm(f => ({ ...f, resource_id: spaceId, physical_space_id: "", space_name: sp?.name || "" }));
    setConflicts(null);
  };
  const checkConflicts = async () => {
    const spaceId = form.physical_space_id || form.resource_id;
    if (!spaceId && !selectedCoach) { toast.error("Select a space or coach first"); return; }
    if (!form.event_date || !form.start_time || !form.end_time) { toast.error("Set date and time first"); return; }
    setCheckingConflicts(true);
    try {
      const res = await api.post("/events/check-conflicts", {
        physical_space_id: form.physical_space_id || undefined, resource_id: form.resource_id || undefined,
        coach_id: selectedCoach?.user_id || undefined, event_date: form.event_date,
        start_time: form.start_time, end_time: form.end_time,
        exclude_event_id: eventId || undefined,
      });
      setConflicts(res.data);
      if (!res.data.has_conflicts) toast.success("No conflicts found!");
    } catch { toast.error("Failed to check conflicts"); }
    setCheckingConflicts(false);
  };

  const buildPayload = () => {
    const payload = { ...form };
    if (!payload.physical_space_id) delete payload.physical_space_id;
    if (!payload.resource_id) delete payload.resource_id;
    if (payload.registration_type === "free") payload.price = 0;
    if (selectedCoach) {
      payload.coach_id = selectedCoach.user_id;
      payload.coach_email = selectedCoach.email;
      if (offer.rate_per_hour) payload.coach_offer_rate_per_hour = parseFloat(offer.rate_per_hour);
      if (offer.revenue_pct) payload.coach_offer_revenue_pct = parseFloat(offer.revenue_pct);
      if (offer.rating_bonus) payload.coach_offer_rating_bonus = parseFloat(offer.rating_bonus);
      payload.coach_offer_rating_threshold = offer.rating_threshold;
      if (offer.notes) payload.coach_offer_notes = offer.notes;
    }
    return payload;
  };

  const validate = () => {
    if (!form.title) { toast.error("Event title is required"); return false; }
    if (!form.venue_id) { toast.error("Select a venue"); return false; }
    if (!form.event_date) { toast.error("Select an event date"); return false; }
    return true;
  };

  // Save as draft
  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await api.put(`/events/${eventId}`, payload);
        toast.success("Event saved!");
      } else {
        await api.post("/events", payload);
        toast.success(selectedCoach ? "Event saved! Offer sent to coach." : "Event saved as draft!");
      }
      navigate("/provider/events");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  // Save & Publish
  const handlePublish = async () => {
    if (!validate()) return;
    setPublishing(true);
    try {
      const payload = buildPayload();
      let eid = eventId;
      if (isEdit) {
        await api.put(`/events/${eventId}`, payload);
      } else {
        const res = await api.post("/events", payload);
        eid = res.data.event_id;
      }
      // Only activate if no coach or coach already approved
      const hasCoach = selectedCoach?.user_id || existingEvent?.coach_id;
      const coachApproved = existingEvent?.coach_approval_status === "approved";
      if (!hasCoach || coachApproved) {
        await api.post(`/events/${eid}/activate`);
        toast.success("Event published!");
      } else {
        toast.success("Event saved! Coach must approve before publishing.");
      }
      navigate("/provider/events");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to publish"); }
    setPublishing(false);
  };

  const calcHours = () => {
    if (!form.start_time || !form.end_time) return 0;
    const [sh, sm] = form.start_time.split(":").map(Number);
    const [eh, em] = form.end_time.split(":").map(Number);
    return Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
  };
  const hours = calcHours();

  if (loadingEvent) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  const pageTitle = isReadOnly ? "View Event" : isEdit ? "Edit Event" : "Organize Event";

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10">
      <button data-testid="back-to-events" onClick={() => navigate("/provider/events")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" />Back to My Events
      </button>
      <div className="flex items-center gap-3 mb-2">
        {isReadOnly ? <Lock className="w-7 h-7 text-slate-400" /> : <Zap className="w-7 h-7 text-court-orange" />}
        <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight" data-testid="organize-event-title">{pageTitle}</h1>
        {isReadOnly && <Badge className="bg-slate-200 text-slate-600 text-[10px] font-heading">READ ONLY</Badge>}
        {isEdit && existingEvent && <Badge className={`text-[10px] font-heading uppercase ${existingEvent.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}`}>
          {existingEvent.status === 'ACTIVE' ? 'Published' : existingEvent.status === 'DRAFT' ? 'Saved' : existingEvent.status}
        </Badge>}
      </div>
      <p className="text-muted-foreground mb-8">
        {isReadOnly ? "This past event cannot be edited." : isEdit ? "Update event details. Changes will notify coaches and attendees." : "Set up a training, class, or clinic at your venue"}
      </p>

      <fieldset disabled={isReadOnly} className="space-y-8">
        {/* ── Section 1: Event Details ── */}
        <section className="bg-white border-2 border-slate-100 p-6" data-testid="section-event-details">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-court-orange" />Event Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Event Title *</Label>
              <Input data-testid="event-title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g., Saturday Basketball Training Camp" className="border-2 rounded-none" />
            </div>
            <div>
              <Label>Event Type</Label>
              <Select value={form.event_type} onValueChange={v => setForm(f => ({ ...f, event_type: v }))}>
                <SelectTrigger data-testid="event-type-select" className="border-2 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>{EVENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sport</Label>
              <Select value={form.sport_type} onValueChange={v => setForm(f => ({ ...f, sport_type: v }))}>
                <SelectTrigger className="border-2 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ALL_SPORTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  <SelectItem value="fitness">Fitness</SelectItem>
                  <SelectItem value="dance">Dance</SelectItem>
                  <SelectItem value="martial_arts">Martial Arts</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea data-testid="event-desc" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Describe the event..." className="border-2 rounded-none" rows={3} />
            </div>
          </div>
        </section>

        {/* ── Section 2: Venue & Space ── */}
        <section className="bg-white border-2 border-slate-100 p-6" data-testid="section-venue-space">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-court-orange" />Venue & Space
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Venue *</Label>
              <Select value={form.venue_id} onValueChange={v => { setForm(f => ({ ...f, venue_id: v, physical_space_id: "", resource_id: "" })); setConflicts(null); }}>
                <SelectTrigger data-testid="venue-select" className="border-2 rounded-none"><SelectValue placeholder="Select venue" /></SelectTrigger>
                <SelectContent>{venues.map(v => <SelectItem key={v.venue_id} value={v.venue_id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Space / Court</Label>
              <Select value={form.physical_space_id || form.resource_id} onValueChange={handleSpaceChange} disabled={spaces.length === 0}>
                <SelectTrigger data-testid="space-select" className="border-2 rounded-none"><SelectValue placeholder={spaces.length ? "Select space" : "Select venue first"} /></SelectTrigger>
                <SelectContent>{spaces.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* ── Section 3: Schedule ── */}
        <section className="bg-white border-2 border-slate-100 p-6" data-testid="section-schedule">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-court-orange" />Schedule
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Date *</Label>
              <Input data-testid="event-date" type="date" value={form.event_date}
                onChange={e => { setForm(f => ({ ...f, event_date: e.target.value })); setConflicts(null); }}
                className="border-2 rounded-none" />
            </div>
            <div>
              <Label>Start Time *</Label>
              <Input data-testid="event-start" type="time" value={form.start_time}
                onChange={e => { setForm(f => ({ ...f, start_time: e.target.value })); setConflicts(null); }}
                className="border-2 rounded-none" />
            </div>
            <div>
              <Label>End Time *</Label>
              <Input data-testid="event-end" type="time" value={form.end_time}
                onChange={e => { setForm(f => ({ ...f, end_time: e.target.value })); setConflicts(null); }}
                className="border-2 rounded-none" />
            </div>
          </div>
          {hours > 0 && <p className="text-xs text-muted-foreground mt-2">Duration: {hours} hour{hours !== 1 ? 's' : ''}</p>}
          {!isReadOnly && (
            <div className="mt-3">
              <Button data-testid="check-conflicts-btn" variant="outline" size="sm" onClick={checkConflicts} disabled={checkingConflicts}
                className="rounded-full text-xs border-2">
                {checkingConflicts ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                Check Conflicts
              </Button>
              {conflicts && (
                <div className={`mt-2 p-3 text-xs border-2 ${conflicts.has_conflicts ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`} data-testid="conflict-result">
                  {conflicts.has_conflicts ? (
                    <div className="space-y-1">
                      <p className="font-bold text-red-700"><AlertTriangle className="w-3 h-3 inline mr-1" />Conflicts Found:</p>
                      {conflicts.space_conflicts?.map((c, i) => <p key={`s-${i}`} className="text-red-600">Space: {c.label} ({c.start?.slice(11, 16)} - {c.end?.slice(11, 16)})</p>)}
                      {conflicts.coach_conflicts?.map((c, i) => <p key={`c-${i}`} className="text-red-600">Coach: {c.label} ({c.start?.slice(11, 16)} - {c.end?.slice(11, 16)})</p>)}
                    </div>
                  ) : <p className="text-green-700"><CheckCircle className="w-3 h-3 inline mr-1" />No conflicts!</p>}
                </div>
              )}
            </div>
          )}
        </section>

        {/* ── Section 4: Coach ── */}
        <section className="bg-white border-2 border-slate-100 p-6" data-testid="section-coach">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-court-orange" />Coach / Sports Professional
          </h2>
          {!selectedCoach ? (
            <div>
              <p className="text-sm text-muted-foreground mb-3">Search for a coach to assign</p>
              <div className="flex gap-2">
                <Input data-testid="coach-search" placeholder="Search coaches by name..." value={coachSearch}
                  onChange={e => setCoachSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && searchCoaches(coachSearch)}
                  className="border-2 rounded-none flex-1" />
                <Button size="sm" onClick={() => searchCoaches(coachSearch)} disabled={searchingCoaches} className="rounded-full px-4">
                  {searchingCoaches ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              {coaches.length > 0 && (
                <div className="mt-3 border-2 border-slate-200 divide-y divide-slate-100 max-h-60 overflow-y-auto">
                  {coaches.map(c => (
                    <button key={c.user_id} onClick={() => selectCoach(c)} className="w-full text-left px-4 py-3 hover:bg-orange-50 transition-colors" data-testid={`coach-option-${c.user_id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-heading font-bold text-sm">{c.name}</span>
                          <span className="text-muted-foreground text-xs ml-2">({c.email})</span>
                          {c.sport_specialties?.length > 0 && <div className="flex gap-1 mt-0.5">{c.sport_specialties.map(s => <Badge key={s} variant="outline" className="text-[9px] font-heading uppercase">{s}</Badge>)}</div>}
                        </div>
                        <div className="text-right">
                          {c.default_price > 0 && <p className="text-xs font-heading text-court-orange">₱{c.default_price}/{c.default_pricing_model === "per_session" ? "session" : "hr"}</p>}
                          {c.coach_rating > 0 && <p className="text-xs text-muted-foreground flex items-center gap-0.5 justify-end"><Star className="w-3 h-3 text-amber-400 fill-amber-400" />{c.coach_rating.toFixed(1)} ({c.total_ratings})</p>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="border-2 border-green-200 bg-green-50/50 p-4 mb-4" data-testid="selected-coach-card">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-court-orange/10 rounded-full flex items-center justify-center"><Award className="w-6 h-6 text-court-orange" /></div>
                    <div>
                      <p className="font-heading font-bold text-base">{selectedCoach.name}</p>
                      <p className="text-xs text-muted-foreground">{selectedCoach.email}</p>
                      <div className="flex items-center gap-3 mt-1">
                        {selectedCoach.coach_rating > 0 && <span className="text-xs flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-400 fill-amber-400" />{selectedCoach.coach_rating.toFixed(1)}</span>}
                        {selectedCoach.default_price > 0 && <span className="text-xs font-heading text-court-orange">Current rate: ₱{selectedCoach.default_price}/hr</span>}
                      </div>
                    </div>
                  </div>
                  {!isReadOnly && <button onClick={clearCoach} className="text-red-500 hover:text-red-700" data-testid="clear-coach-btn"><XCircle className="w-5 h-5" /></button>}
                </div>
              </div>
              {!showOfferForm && !isReadOnly ? (
                <Button data-testid="open-offer-form-btn" onClick={() => setShowOfferForm(true)}
                  className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase text-xs px-6 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                  <Send className="w-4 h-4 mr-1" />Prepare Coach Offer Request
                </Button>
              ) : showOfferForm && (
                <CoachOfferForm coach={selectedCoach} form={form} hours={hours} offer={offer} setOffer={setOffer} venues={venues} readOnly={isReadOnly} />
              )}
            </div>
          )}
        </section>

        {/* ── Section 5: Capacity & Pricing ── */}
        <section className="bg-white border-2 border-slate-100 p-6" data-testid="section-pricing">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
            <Banknote className="w-5 h-5 text-court-orange" />Capacity & Pricing
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Max Participants</Label>
              <Input data-testid="event-capacity" type="number" min={1} value={form.capacity}
                onChange={e => setForm(f => ({ ...f, capacity: parseInt(e.target.value) || 1 }))} className="border-2 rounded-none" />
            </div>
            <div>
              <Label>Registration</Label>
              <Select value={form.registration_type} onValueChange={v => setForm(f => ({ ...f, registration_type: v, price: v === "free" ? 0 : f.price }))}>
                <SelectTrigger data-testid="reg-type" className="border-2 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="free">Free</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent>
              </Select>
            </div>
            {form.registration_type === "paid" && (
              <div><Label>Price per person (₱)</Label>
                <Input data-testid="event-price" type="number" min={0} value={form.price}
                  onChange={e => setForm(f => ({ ...f, price: parseFloat(e.target.value) || 0 }))} className="border-2 rounded-none" />
              </div>
            )}
          </div>
        </section>

        {/* ── Section 6: Waiver Custom Terms ── */}
        <section className="bg-white border-2 border-slate-100 p-6" data-testid="section-waiver-terms">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-2 flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-court-orange" />Waiver & Consent
          </h2>
          <p className="text-sm text-muted-foreground mb-4">A standard waiver is shown to guardians of minor attendees. Add custom terms if needed.</p>
          <div>
            <Label>Custom Waiver Terms (optional)</Label>
            <Textarea data-testid="custom-waiver-terms" value={form.custom_waiver_terms}
              onChange={e => setForm(f => ({ ...f, custom_waiver_terms: e.target.value }))}
              placeholder="Add any additional terms, venue-specific rules, or medical requirements..."
              className="border-2 rounded-none" rows={3} />
            <p className="text-[10px] text-muted-foreground mt-1">These will appear under "Additional Terms" in the waiver form.</p>
          </div>
        </section>
      </fieldset>

      {/* ── Share Registration Link (Published Events) ── */}
      {isEdit && existingEvent?.status === "ACTIVE" && (
        <ShareLinkSection eventId={eventId} eventTitle={existingEvent?.title || form.title} />
      )}

      {/* ── Attendee List (Published Events) ── */}
      {isEdit && existingEvent?.status === "ACTIVE" && (
        <section className="bg-white border-2 border-slate-100 p-6 mt-8" data-testid="section-attendees">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-court-orange" />Attendees ({attendeeMeta.total}/{attendeeMeta.capacity})
          </h2>
          {loadingAttendees ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-court-orange" /></div>
          ) : attendees.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-slate-200">
              <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No attendees registered yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="attendee-table">
                <thead>
                  <tr className="border-b-2 border-slate-200 text-left">
                    <th className="font-heading text-[10px] uppercase tracking-[0.15em] text-muted-foreground pb-2 pr-4">#</th>
                    <th className="font-heading text-[10px] uppercase tracking-[0.15em] text-muted-foreground pb-2 pr-4">Name</th>
                    <th className="font-heading text-[10px] uppercase tracking-[0.15em] text-muted-foreground pb-2 pr-4">Age</th>
                    <th className="font-heading text-[10px] uppercase tracking-[0.15em] text-muted-foreground pb-2 pr-4">Guardian</th>
                    <th className="font-heading text-[10px] uppercase tracking-[0.15em] text-muted-foreground pb-2 pr-4">Payment</th>
                    <th className="font-heading text-[10px] uppercase tracking-[0.15em] text-muted-foreground pb-2 pr-4">Member</th>
                    <th className="font-heading text-[10px] uppercase tracking-[0.15em] text-muted-foreground pb-2">Waiver</th>
                  </tr>
                </thead>
                <tbody>
                  {attendees.map((a, i) => (
                    <tr key={a.registration_id} className="border-b border-slate-100" data-testid={`attendee-row-${i}`}>
                      <td className="py-2.5 pr-4 text-muted-foreground text-xs">{i + 1}</td>
                      <td className="py-2.5 pr-4">
                        <p className="font-medium">{a.name}</p>
                        <p className="text-[10px] text-muted-foreground">{a.email}</p>
                        {a.phone && <p className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{a.phone}</p>}
                      </td>
                      <td className="py-2.5 pr-4">
                        {a.age != null ? (
                          <span className={a.is_minor ? "text-amber-600 font-medium" : ""}>{a.age} {a.is_minor && "(Minor)"}</span>
                        ) : <span className="text-muted-foreground text-xs">—</span>}
                      </td>
                      <td className="py-2.5 pr-4 text-xs">
                        {a.is_minor ? (
                          a.guardian_name ? (
                            <div>
                              <p className="font-medium">{a.guardian_name}</p>
                              {a.guardian_contact && <p className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{a.guardian_contact}</p>}
                            </div>
                          ) : <span className="text-red-500 text-[10px]">Not provided</span>
                        ) : <span className="text-muted-foreground">N/A</span>}
                      </td>
                      <td className="py-2.5 pr-4">
                        <Badge className={`text-[9px] font-heading uppercase ${a.payment_status === "Paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                          {a.payment_status}
                        </Badge>
                        {a.amount > 0 && a.payment_status === "Paid" && <p className="text-[10px] text-muted-foreground mt-0.5">₱{a.amount}</p>}
                      </td>
                      <td className="py-2.5 pr-4">
                        {a.is_setplay_member ? (
                          <Badge className="bg-court-orange/10 text-court-orange text-[9px] font-heading uppercase flex items-center gap-0.5 w-fit">
                            <ShieldCheck className="w-3 h-3" />Member
                          </Badge>
                        ) : <span className="text-muted-foreground text-[10px]">Guest</span>}
                      </td>
                      <td className="py-2.5">
                        {a.waiver_signed ? (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/events/${eventId}/registrations/${a.registration_id}/waiver?token=${localStorage.getItem('setplay_session_token') || ''}`}
                            target="_blank" rel="noopener noreferrer" data-testid={`waiver-download-${i}`}
                            className="inline-flex items-center gap-1 text-[10px] text-court-orange hover:underline font-heading uppercase">
                            <Download className="w-3 h-3" />Waiver
                          </a>
                        ) : a.is_minor ? (
                          <span className="text-red-500 text-[10px]">Missing</span>
                        ) : (
                          <span className="text-muted-foreground text-[10px]">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ── Action Bar ── */}
      {!isReadOnly && (
        <div className="flex items-center gap-4 justify-between bg-white border-2 border-slate-200 p-6 mt-8" data-testid="submit-bar">
          <Button variant="outline" onClick={() => navigate("/provider/events")} className="rounded-full border-2">Cancel</Button>
          <div className="flex items-center gap-3">
            {selectedCoach && showOfferForm && (
              <p className="text-xs text-muted-foreground hidden md:block">
                Offer will be sent to <strong>{selectedCoach.name}</strong>
              </p>
            )}
            <Button data-testid="save-event-btn" onClick={handleSave} disabled={saving || publishing} variant="outline"
              className="rounded-full border-2 font-heading uppercase text-xs px-6">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button data-testid="publish-event-btn" onClick={handlePublish} disabled={saving || publishing}
              className="bg-green-600 hover:bg-green-700 text-white rounded-full font-heading uppercase text-xs px-6 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
              {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              {publishing ? "Publishing..." : "Save & Publish"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Coach Offer Request Form ── */
function CoachOfferForm({ coach, form, hours, offer, setOffer, venues, readOnly }) {
  const venueName = venues.find(v => v.venue_id === form.venue_id)?.name || "";
  const estimatedRevenue = form.registration_type === "paid" && form.price > 0 ? form.price * form.capacity : 0;

  return (
    <div className="border-2 border-court-orange/30 bg-orange-50/30 p-5 space-y-5" data-testid="coach-offer-form">
      <div className="flex items-center gap-2 mb-1">
        <Send className="w-5 h-5 text-court-orange" />
        <h3 className="font-heading text-base font-bold uppercase tracking-tight">Coach Offer Request</h3>
      </div>
      <div className="bg-white border border-slate-200 p-4 space-y-2 text-sm" data-testid="offer-event-summary">
        <p className="font-heading text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Event Summary</p>
        <div className="grid grid-cols-2 gap-2">
          <div><span className="text-muted-foreground">Event:</span> <strong>{form.title || "—"}</strong></div>
          <div><span className="text-muted-foreground">Type:</span> <strong className="capitalize">{form.event_type}</strong></div>
          <div><span className="text-muted-foreground">Venue:</span> <strong>{venueName || "—"}</strong></div>
          <div><span className="text-muted-foreground">Space:</span> <strong>{form.space_name || "—"}</strong></div>
          <div><span className="text-muted-foreground">Date:</span> <strong>{form.event_date || "—"}</strong></div>
          <div><span className="text-muted-foreground">Time:</span> <strong>{form.start_time} - {form.end_time} ({hours}h)</strong></div>
          <div><span className="text-muted-foreground">Capacity:</span> <strong>{form.capacity} participants</strong></div>
          <div><span className="text-muted-foreground">Registration:</span> <strong>{form.registration_type === "free" ? "Free" : `₱${form.price}/person`}</strong></div>
        </div>
        {estimatedRevenue > 0 && <p className="text-xs text-muted-foreground border-t border-slate-100 pt-2 mt-2">Estimated max revenue: <strong className="text-court-orange">₱{estimatedRevenue.toLocaleString()}</strong></p>}
      </div>
      {coach.default_price > 0 && (
        <div className="bg-blue-50 border border-blue-200 p-3 flex items-center gap-3" data-testid="coach-current-rate">
          <Banknote className="w-5 h-5 text-blue-500 shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-800">{coach.name}'s current rate: <strong>₱{coach.default_price}</strong>/hr</p>
            {coach.coach_rating > 0 && <p className="text-xs text-blue-600 flex items-center gap-1 mt-0.5"><Star className="w-3 h-3 text-amber-400 fill-amber-400" />{coach.coach_rating.toFixed(1)} rating</p>}
          </div>
        </div>
      )}
      <Separator />
      <div>
        <p className="font-heading text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">Compensation Offer</p>
        <div className="space-y-4">
          <div className="flex items-start gap-3 bg-white border border-slate-200 p-4" data-testid="offer-rate-section">
            <Banknote className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <Label className="font-heading text-sm font-bold">Rate per Hour (₱)</Label>
              <p className="text-[10px] text-muted-foreground mb-2">Fixed hourly rate</p>
              <Input data-testid="offer-rate" type="number" min={0} placeholder={coach.default_price ? `Coach rate: ₱${coach.default_price}` : "e.g., 500"}
                value={offer.rate_per_hour} onChange={e => setOffer(o => ({ ...o, rate_per_hour: e.target.value }))} className="border-2 rounded-none w-48" disabled={readOnly} />
              {offer.rate_per_hour && hours > 0 && <p className="text-xs text-green-700 mt-1">Total: ₱{(parseFloat(offer.rate_per_hour) * hours).toLocaleString()} for {hours}h</p>}
            </div>
          </div>
          <div className="flex items-start gap-3 bg-white border border-slate-200 p-4" data-testid="offer-revenue-section">
            <Percent className="w-5 h-5 text-purple-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <Label className="font-heading text-sm font-bold">Revenue Share (%)</Label>
              <p className="text-[10px] text-muted-foreground mb-2">Percentage of total event revenue</p>
              <Input data-testid="offer-revenue-pct" type="number" min={0} max={100} placeholder="e.g., 15"
                value={offer.revenue_pct} onChange={e => setOffer(o => ({ ...o, revenue_pct: e.target.value }))} className="border-2 rounded-none w-48" disabled={readOnly} />
              {offer.revenue_pct && estimatedRevenue > 0 && <p className="text-xs text-purple-700 mt-1">Estimated: ₱{((parseFloat(offer.revenue_pct) / 100) * estimatedRevenue).toLocaleString()}</p>}
            </div>
          </div>
          <div className="flex items-start gap-3 bg-white border border-slate-200 p-4" data-testid="offer-bonus-section">
            <Star className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
            <div className="flex-1">
              <Label className="font-heading text-sm font-bold">Performance Bonus (₱)</Label>
              <p className="text-[10px] text-muted-foreground mb-2">Bonus for high participant rating</p>
              <div className="flex items-center gap-3">
                <Input data-testid="offer-rating-bonus" type="number" min={0} placeholder="e.g., 1000"
                  value={offer.rating_bonus} onChange={e => setOffer(o => ({ ...o, rating_bonus: e.target.value }))} className="border-2 rounded-none w-48" disabled={readOnly} />
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">for</span>
                  <Select value={offer.rating_threshold.toFixed(1)} onValueChange={v => setOffer(o => ({ ...o, rating_threshold: parseFloat(v) }))} disabled={readOnly}>
                    <SelectTrigger className="border-2 rounded-none w-24 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3.5">3.5★+</SelectItem>
                      <SelectItem value="4.0">4.0★+</SelectItem>
                      <SelectItem value="4.5">4.5★+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          <div>
            <Label className="font-heading text-sm font-bold">Additional Notes</Label>
            <Textarea data-testid="offer-notes" value={offer.notes} onChange={e => setOffer(o => ({ ...o, notes: e.target.value }))}
              placeholder="Terms, expectations..." className="border-2 rounded-none mt-1" rows={2} disabled={readOnly} />
          </div>
        </div>
      </div>
      {(offer.rate_per_hour || offer.revenue_pct || offer.rating_bonus) && (
        <div className="bg-court-orange/5 border-2 border-court-orange/20 p-4" data-testid="offer-summary">
          <p className="font-heading text-xs uppercase tracking-[0.15em] text-court-orange mb-2">Offer Summary for {coach.name}</p>
          <div className="space-y-1 text-sm">
            {offer.rate_per_hour && <p className="flex items-center gap-2"><Banknote className="w-3.5 h-3.5 text-green-600" />₱{parseFloat(offer.rate_per_hour).toLocaleString()}/hr{hours > 0 ? ` (₱${(parseFloat(offer.rate_per_hour) * hours).toLocaleString()} total)` : ''}</p>}
            {offer.revenue_pct && <p className="flex items-center gap-2"><Percent className="w-3.5 h-3.5 text-purple-600" />{offer.revenue_pct}% of total revenue</p>}
            {offer.rating_bonus && <p className="flex items-center gap-2"><Star className="w-3.5 h-3.5 text-amber-500" />₱{parseFloat(offer.rating_bonus).toLocaleString()} bonus for {offer.rating_threshold}★+</p>}
          </div>
        </div>
      )}
    </div>
  );
}


/* ── Share Link Section ── */
function ShareLinkSection({ eventId, eventTitle }) {
  const publicUrl = `${window.location.origin}/events/${eventId}/register`;
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true); toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error("Failed to copy"));
  };

  const shareToFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}`, "_blank");
  const shareToTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Join ${eventTitle}!`)}&url=${encodeURIComponent(publicUrl)}`, "_blank");

  return (
    <section className="bg-green-50 border-2 border-green-200 p-6 mt-8" data-testid="section-share-link">
      <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-2 flex items-center gap-2">
        <Share2 className="w-5 h-5 text-green-600" />Share Registration Link
      </h2>
      <p className="text-sm text-muted-foreground mb-4">Share this link so anyone can register for your event — no SetPlay account required.</p>
      <div className="flex items-center gap-2">
        <Input data-testid="share-link-input" readOnly value={publicUrl} className="border-2 rounded-none bg-white flex-1 text-sm font-mono" />
        <Button data-testid="copy-link-btn" onClick={copyLink} variant="outline" size="sm" className="rounded-full border-2 shrink-0 px-4">
          {copied ? <CheckCircle className="w-4 h-4 text-green-600 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
          {copied ? "Copied!" : "Copy"}
        </Button>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <span className="text-xs text-muted-foreground">Share to:</span>
        <Button size="sm" variant="outline" onClick={shareToFacebook} className="rounded-full text-[10px] border px-3 h-7">Facebook</Button>
        <Button size="sm" variant="outline" onClick={shareToTwitter} className="rounded-full text-[10px] border px-3 h-7">Twitter / X</Button>
        <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-court-orange hover:underline ml-2">
          <ExternalLink className="w-3 h-3" />Preview
        </a>
      </div>
    </section>
  );
}
